import { useEffect, useMemo, useState } from 'react';

import { useGetWalletBalanceQuery, useGetWalletTransactionsQuery } from '../../services/wallet.api';
import { PaginationControls, Card, Heading } from '../../shared/ui';

interface WalletTransaction {
  id: string;
  date: string;
  description: string;
  type: 'credit' | 'debit' | 'bonus';
  amount: number;
  balanceAfter: number;
  timestamp: number;
  rawType: number;
}

interface WalletProps {
  refreshKey?: number;
}

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const mapTxType = (tx: { type: number; source_type: string }): WalletTransaction['type'] => {
  switch (tx.type) {
    case 0:
      return 'debit';
    case 1:
      return tx.source_type === 'referral_registration' ? 'bonus' : 'credit';
    case 2:
    case 3:
      return 'bonus';
    default:
      return 'credit';
  }
};

export function Wallet({ refreshKey }: WalletProps) {
  const {
    data: balanceData,
    isLoading: isBalanceLoading,
    isError: isBalanceError,
    refetch: refetchBalance,
  } = useGetWalletBalanceQuery();
  const {
    data: transactionsData,
    isLoading: isTransactionsLoading,
    isError: isTransactionsError,
    refetch: refetchTransactions,
  } = useGetWalletTransactionsQuery();

  useEffect(() => {
    if (refreshKey === undefined) return;
    refetchBalance();
    refetchTransactions();
  }, [refreshKey, refetchBalance, refetchTransactions]);

  const walletBalance = balanceData?.available ?? 0;
  const mapTransactions = (transactionsData?.transactions ?? [])
    .filter((tx) => tx.type !== 2 && tx.type !== 3)
    .map((tx) => {
      const timestamp = new Date(tx.operation_date).getTime();
      return {
        id: tx.id,
        date: formatDate(tx.operation_date),
        description: tx.description,
        type: mapTxType(tx),
        amount: Number(tx.amount),
        balanceAfter: Number(tx.balance_after),
        rawType: tx.type,
        timestamp,
      };
    });

  const transactions = mapTransactions.length > 0 ? mapTransactions.sort((a, b) => b.timestamp - a.timestamp) : [];

  const itemsPerPage = 5;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(transactions.length / itemsPerPage));
  const displayedTransactions = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return transactions.slice(start, start + itemsPerPage);
  }, [page, transactions]);
  useEffect(() => {
    setPage(1);
  }, [transactions.length]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);
  const loading = isBalanceLoading || isTransactionsLoading;
  const error = isBalanceError || isTransactionsError;
  const balanceLabel = error ? 'Ошибка загрузки' : 'Доступно';
  const balanceValue = loading ? '—' : walletBalance.toLocaleString();
  const hasTransactions = transactions.length > 0;
  const showEmptyState = !hasTransactions && !loading && !error;

  const totalsSource = transactions;
  const totalCredit = totalsSource.filter((t) => t.type === 'credit').reduce((sum, t) => sum + t.amount, 0);
  const totalDebit = totalsSource.filter((t) => t.type === 'debit').reduce((sum, t) => sum + t.amount, 0);
  const totalBonus = totalsSource.filter((t) => t.type === 'bonus').reduce((sum, t) => sum + t.amount, 0);

  const formatAmount = (value: number) => new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(value);

  const getTransactionIcon = (type: WalletTransaction['type']) => {
    switch (type) {
      case 'credit':
        return <ArrowDownIcon />;
      case 'debit':
        return <ArrowUpIcon />;
      case 'bonus':
        return <GiftIcon />;
    }
  };

  const getTransactionBadge = (type: WalletTransaction['type']) => {
    switch (type) {
      case 'credit':
        return <span className="px-3 py-1 text-ui-xs-strong rounded-full bg-success text-white">Пополнение</span>;
      case 'debit':
        return <span className="px-3 py-1 text-ui-xs-strong rounded-full bg-primary text-white">Списание</span>;
      case 'bonus':
        return <span className="px-3 py-1 text-ui-xs-strong rounded-full bg-accent text-ink">Бонус</span>;
    }
  };

  return (
    <div className="space-y-6">
      <Card variant="raised">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <WalletIcon />
              <Heading size="sm">Бонусный баланс</Heading>
            </div>
            <p className="text-ink-muted text-ui">Используйте бонусные рубли для оплаты подписок и аддонов</p>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-ink-muted text-ui mb-2">{balanceLabel}</p>
          <p className="text-display-l text-ink mb-1">
            {balanceValue !== '—' ? formatAmount(walletBalance) : balanceValue}
            {balanceValue !== '—' && ' ₽'}
          </p>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-card border border-surface-muted bg-surface-muted p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white rounded-control border border-surface-muted text-success">
              <ArrowDownIcon />
            </div>
            <div>
              <p className="text-ui text-ink-muted">Всего пополнено</p>
              <p className="text-display-s font-normal text-ink">{formatAmount(totalCredit)} ₽</p>
            </div>
          </div>
        </div>

        <div className="rounded-card border border-surface-muted bg-surface-muted p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white rounded-control border border-surface-muted text-primary">
              <ArrowUpIcon />
            </div>
            <div>
              <p className="text-ui text-ink-muted">Всего потрачено</p>
              <p className="text-display-s font-normal text-ink">{formatAmount(totalDebit)} ₽</p>
            </div>
          </div>
        </div>

        <div className="rounded-card border border-surface-muted bg-surface-muted p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white rounded-control border border-surface-muted text-accent">
              <GiftIcon />
            </div>
            <div>
              <p className="text-ui text-ink-muted">Бонусов начислено</p>
              <p className="text-display-s font-normal text-ink">{formatAmount(totalBonus)} ₽</p>
            </div>
          </div>
        </div>
      </div>

      <Card variant="raised">
        <Heading size="sm" className="mb-6">
          История транзакций
        </Heading>
        <div className="space-y-4">
          {hasTransactions &&
            displayedTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="p-4 border border-surface-muted rounded-card hover:bg-surface-muted transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-1">{getTransactionIcon(transaction.type)}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Heading size="sm" as="h4">
                          {transaction.description}
                        </Heading>
                        {getTransactionBadge(transaction.type)}
                      </div>
                      <p className="text-ui text-ink-muted mb-2">{transaction.date}</p>
                      <p className="text-ui-xs text-ink-subtle">
                        Баланс после операции: {formatAmount(transaction.balanceAfter)} ₽
                      </p>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p
                      className={`text-display-s font-normal ${
                        transaction.rawType === 2 || transaction.rawType === 3
                          ? 'text-ink-subtle'
                          : transaction.type === 'debit'
                            ? 'text-primary'
                            : 'text-success'
                      }`}
                    >
                      {transaction.type === 'debit' || transaction.rawType === 2 ? '-' : '+'}
                      {formatAmount(transaction.amount)} ₽
                    </p>
                  </div>
                </div>
              </div>
            ))}
          {loading && (
            <div className="text-center py-12">
              <p className="text-ink-muted">Загружаем транзакции...</p>
            </div>
          )}
          {error && (
            <div className="text-center py-12">
              <p className="text-ink-muted">Не удалось загрузить транзакции</p>
            </div>
          )}
          {showEmptyState && (
            <div className="text-center py-12">
              <WalletIcon className="mx-auto mb-3" />
              <p className="text-ink-muted">Нет транзакций</p>
            </div>
          )}
        </div>

        {transactions.length > itemsPerPage && !loading && !error && (
          <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} className="mt-4" />
        )}
      </Card>

      <div className="rounded-card border border-info-line bg-info-surface p-6">
        <Heading size="sm" className="mb-4">
          Как работают бонусные рубли?
        </Heading>
        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className="size-6 rounded-full bg-primary text-white flex items-center justify-center text-ui">
                1
              </div>
            </div>
            <div>
              <Heading size="sm" as="h4" className="mb-1">
                Получайте бонусы
              </Heading>
              <p className="text-ui text-ink-muted">
                Привлекайте друзей и участвуйте в спецакциях, чтобы заработать бонусные рубли.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className="size-6 rounded-full bg-primary text-white flex items-center justify-center text-ui">
                2
              </div>
            </div>
            <div>
              <Heading size="sm" as="h4" className="mb-1">
                Оплачивайте покупки
              </Heading>
              <p className="text-ui text-ink-muted">
                Используйте бонусные рубли для подписок, дополнений и других услуг.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const WalletIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={`size-6 text-primary ${className ?? ''}`} aria-hidden="true">
    <path
      d="M4 7.5h14a2 2 0 012 2v7a2 2 0 01-2 2H6a2 2 0 01-2-2v-9z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M4 7.5h12a2 2 0 012 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="16.5" cy="12.5" r="1.5" fill="currentColor" />
  </svg>
);

const ArrowDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-5 text-success" aria-hidden="true">
    <path d="M12 5v14m0 0l-5-5m5 5l5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-5 text-primary" aria-hidden="true">
    <path d="M12 19V5m0 0l-5 5m5-5l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const GiftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-5 text-accent" aria-hidden="true">
    <path
      d="M4 11h16v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M12 11v10M4 7h16v4H4V7z" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M8 7c-1.1 0-2-.9-2-2 0-1.5 1.8-2.2 3-1 1.2 1.2 1.4 3 .4 3H8zm8 0c1.1 0 2-.9 2-2 0-1.5-1.8-2.2-3-1-1.2 1.2-1.4 3-.4 3h1.4z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-4 mr-2" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
